/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import { isEqual } from 'lodash';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { typeformLinkFromUser } from './feedbackForm';
// eslint-disable-next-line import/no-cycle
import { useStoreState } from '../reducers';

/**
 * Hook which takes care of calling a function whenever the user clicks
 * outside of an HTML element on the page. Also makes sure to remove any
 * event listener whenever the component is removed from the page, or
 * when the HTML ref object is modified.
 * @param ref React Reference to the HTML element
 * @param onClickedOutside Callback to call when user clicked outside
 * the element
 * @param closeOnClickOutside
 */
function useOutsideCallback(
  ref: React.RefObject<any>,
  onClickedOutside: () => void,
  closeOnClickOutside = true
) {
  useEffect(() => {
    // When clicked outside of the ref, call the callback
    function handleClickOutside(event: MouseEvent) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      if (closeOnClickOutside && !ref.current?.contains(event.target)) {
        onClickedOutside();
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, onClickedOutside, closeOnClickOutside]);
}

/**
 * Hook which returns the value of a variable during the previous render
 * to this one
 *
 * Inspired from Hooks FAQ
 * https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state
 * @param someVariable
 * @returns value of someVariable in the previous render to this one
 */
function usePrevious<T>(someVariable: T) {
  const ref = useRef<T | null>(null);

  // Schedule an effect to run after this render has finished
  useEffect(() => {
    ref.current = someVariable;
  }, [someVariable]);

  // Return previous variable value (happens before update in useEffect above)
  return ref.current;
}

/**
 * Hook very similar to useEffect (same signature) but which will make a
 * deep comparison of the elements in the dependency array instead of a
 * shallow equality used by regular useEffect. It will only run the
 * effect if the dependencies have changed deeply.
 *
 * This is useful when JS objects in the dependency array are updated
 * by, say, a reducer, but the object content itself hasn't changed.
 * @param effect Effect to run (same as function passed to useEffect)
 * @param deps Dependency array for the hook (same as useEffect)
 */
function useDeepCompareEffect(
  effect: React.EffectCallback,
  deps?: React.DependencyList | undefined
) {
  const depsRef = useRef<React.DependencyList | undefined>();

  if (!isEqual(deps, depsRef.current)) {
    depsRef.current = deps;
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(effect, depsRef.current);
}

/**
 * Hook which works the same as a regular useEffect except that it will
 * not run on the very first component render but will run for every
 * subsequent dependency array change
 * @param effect Effect to run (same as function passed to useEffect)
 * @param deps Dependency array for the hook (same as useEffect)
 */
function useEffectExceptFirstRender(
  effect: React.EffectCallback,
  deps?: React.DependencyList | undefined
) {
  const isFirstRender = useRef(true);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!isFirstRender.current) return effect();
    isFirstRender.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * Hook ensuring that the provided callback will be called on every new
 * frame (i.e. on every render to the screen), before the browser
 * paints.
 *
 * Callbacks will be queued as animation requests using the
 * requestAnimationFrame method provided by the browser
 * @param callback
 */
const useAnimationFrame = (callback: (timeMs: number) => void) => {
  // Use references so we don't have to modify the animation loop below
  const callbackRef = useRef(callback);
  const frameRef = useRef<number>(0);

  /* 
    useLayoutEffect is used here previously. 
    switch to useEffect for the performance improve and due to the frame play
    nature, there should be no visual impact on that.
  */
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const loop = (timeMs: number) => {
      const cb = callbackRef.current;
      cb(timeMs);

      frameRef.current = requestAnimationFrame(loop);
    };
    frameRef.current = requestAnimationFrame(loop);

    // Stop the animation loop when component unmounts
    return () => cancelAnimationFrame(frameRef.current);
  }, []);
};

/**
 * Hook ensuring that the provided callback will be called before the
 * browser paints, at a given framerate.
 *
 * Callbacks will be queued as animation requests using the
 * requestAnimationFrame method provided by the browser
 * @param callback
 * @param frameRate Number of times we want the callback to be called
 * per second
 */
const useAnimationFrameThrottled = (
  callback: () => void,
  frameRate: number
) => {
  const lastTs = useRef(performance.now());
  const targetDelta = 1000 / frameRate;

  useAnimationFrame((ts) => {
    const elapsed = ts - lastTs.current;

    if (elapsed >= targetDelta) {
      // FIX: Snap to current time to prevent "Time Debt" accumulation.
      // If the browser froze for 200ms, we don't want to fire 10 times instantly.
      lastTs.current = ts - (elapsed % targetDelta);
      callback();
    }
  });
};

/**
 * Hook providing the form URL for users to provide feedback
 *
 * The form URL will have interesting metadata in its fragment such as
 * username and source URL to help with troubleshooting down the line.
 */
const useFeedbackForm = () => {
  const location = useLocation();
  const { user, driveInfo } = useStoreState((store) => ({
    ...store.user,
    ...store.drive,
  }));

  const driveRouteMatch = location.pathname.match(
    /^\/organization\/([^/]+)\/drive\/([^/]+)/
  );

  if (driveRouteMatch) {
    const routeOrgId = driveRouteMatch[1];
    const routeDriveId = driveRouteMatch[2];
    const driveContext = {
      id: driveInfo?.id ?? routeDriveId,
      orgId: driveInfo?.organizationId ?? routeOrgId,
      recordingTitle: driveInfo?.recordingTitle ?? null,
      dataCategory: driveInfo?.dataCategory ?? null,
      vizStatus: driveInfo?.visualization_status ?? null,
      osfStatus: driveInfo?.osfGenerationStatus ?? null,
    };

    return typeformLinkFromUser(user, location.pathname, {
      drive_id: String(driveContext.id),
      org_id: String(driveContext.orgId),
      drive_info: JSON.stringify(driveContext),
    });
  }

  return typeformLinkFromUser(user, location.pathname);
};

export {
  useOutsideCallback,
  usePrevious,
  useEffectExceptFirstRender,
  useDeepCompareEffect,
  useAnimationFrame,
  useAnimationFrameThrottled,
  useFeedbackForm,
};
